import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  Box,
  Chip,
  Button,
  Checkbox,
  Toolbar,
  alpha,
  Card,
  CardContent,
  CardActions,
  Divider,
  Stack,
  Grid } from
'@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as OnlineIcon,
  Error as OfflineIcon,
  Warning as WarningIcon,
  NetworkPing as PingIcon,
  Refresh as RefreshIcon } from
'@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionContext';
import { formatDateTime } from '../../utils/dateUtils';
import { devicesApi } from '../../services/apiClient';
import { useIsMobile } from '../../hooks/useResponsive';
import { useThemeSettings } from "../../contexts/ThemeContext";
import { getDeviceLanIP, getDeviceWanIP } from '../../utils/deviceHelpers';
import { DEVICE_TYPES } from '../../config/constants';
import HexnodeActions from '../HexnodeActions';

const DeviceTable = ({ locationId, onEditDevice, onDeleteDevice, devices: initialDevices = null, loading: initialLoading = false, onRefresh }) => {const { t } = useThemeSettings();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(initialLoading);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [pingStatus, setPingStatus] = useState({});
  const [pinging, setPinging] = useState({});

  const { canManageDevices } = usePermissions();
  const isMobile = useIsMobile();

  useEffect(() => {
    // If devices are provided directly, use them
    if (initialDevices) {
      setDevices(Array.isArray(initialDevices) ? initialDevices : []);
      setLoading(false);
    } else if (locationId) {
      // If locationId is provided but no devices, fetch them
      fetchDevicesForLocation(locationId);
    }
  }, [locationId, initialDevices]);

  const fetchDevicesForLocation = async (locId) => {
    try {
      setLoading(true);
      // This function would be provided by props or a context
      // Using a mock response for now based on your API data
      const response = await fetch(`/api/locations/${locId}/devices`);
      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        setDevices(result.data);
      } else {
        setDevices([]);
      }
      setError(null);
    } catch (err) {
      setError('Failed to load devices. Please try again.');
      console.error('Error loading devices:', err);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (deviceId) => {
    try {
      // Call API to delete device
      await fetch(`/api/devices/${deviceId}`, {
        method: 'DELETE'
      });

      // Remove from local state
      setDevices(devices.filter((device) => device.id !== deviceId));

      // Callback
      if (onDeleteDevice) onDeleteDevice(deviceId);
    } catch (err) {
      setError('Failed to delete device. Please try again.');
      console.error('Error deleting device:', err);
    }
  };

  const getStatusChip = (config) => {const { t } = useThemeSettings();
    // Get status from config object
    const status = config?.status || 'Unknown';
    const statusLower = status.toLowerCase();

    // Determine color and icon based on status
    let color = 'default';
    let icon = <WarningIcon />;

    if (['registered', 'online', 'active'].includes(statusLower)) {
      color = 'success';
      icon = <OnlineIcon />;
    } else if (['offline', 'inactive', 'disconnected'].includes(statusLower)) {
      color = 'error';
      icon = <OfflineIcon />;
    } else {
      color = 'warning';
      icon = <WarningIcon />;
    }

    return (
      <Chip
        icon={icon}
        label={status}
        color={color}
        size="small"
        variant="outlined" />);


  };

  // Helper to get device name (fallback to ID if no name)
  const getDeviceName = (device) => {
    return device.friendlyName || `Device ${device.id}`;
  };

  // Handle single device ping
  const handlePingDevice = async (deviceId) => {
    setPinging((prev) => ({ ...prev, [deviceId]: true }));

    try {
      const response = await devicesApi.ping(deviceId);

      if (response.data.success) {
        setPingStatus((prev) => ({
          ...prev,
          [deviceId]: {
            status: response.data.status,
            responseTime: response.data.responseTime,
            timestamp: new Date().toISOString()
          }
        }));

        // Update device status if needed
        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (error) {
      console.error('Ping failed:', error);
      setPingStatus((prev) => ({
        ...prev,
        [deviceId]: {
          status: 'failed',
          error: error.message,
          timestamp: new Date().toISOString()
        }
      }));
    } finally {
      setPinging((prev) => ({ ...prev, [deviceId]: false }));
    }
  };

  // Handle batch ping for selected devices
  const handlePingSelected = async () => {
    if (selected.length === 0) return;

    try {
      const response = await devicesApi.pingBatch(selected);

      if (response.data.results) {
        const newPingStatus = {};
        response.data.results.forEach((result) => {
          newPingStatus[result.deviceId] = {
            status: result.status,
            responseTime: result.responseTime,
            timestamp: new Date().toISOString()
          };
        });
        setPingStatus(newPingStatus);

        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (error) {
      console.error('Batch ping failed:', error);
    }
  };

  // Handle checkbox selection
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = devices.map((d) => d.id || d._id || d.device_UUID);
      setSelected(newSelected);
    } else {
      setSelected([]);
    }
  };

  const handleSelectDevice = (deviceId) => {
    const selectedIndex = selected.indexOf(deviceId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, deviceId];
    } else {
      newSelected = selected.filter((id) => id !== deviceId);
    }

    setSelected(newSelected);
  };

  const isSelected = (deviceId) => selected.indexOf(deviceId) !== -1;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>);

  }

  if (error) {
    return (
      <Box p={2}>
        <Typography color="error">{error}</Typography>
      </Box>);

  }

  // Mobile Card View
  const renderMobileView = () =>
  <Box>
      {selected.length > 0 &&
    <Paper sx={{ p: 2, mb: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1) }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" color="primary">
              {selected.length}{t ? t("DeviceTable.selected") : "selected"}
        </Typography>
            <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handlePingSelected}
          size="small">{t ? t("DeviceTable.ping_all") : "Ping All"}


        </Button>
          </Stack>
        </Paper>
    }

      {devices.length === 0 ?
    <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">{t ? t("DeviceTable.no_devices_found") : "No devices found"}</Typography>
        </Paper> :

    devices.map((device) => {
      const deviceId = device.id || device._id || device.device_UUID;
      const isItemSelected = isSelected(deviceId);
      const pingInfo = pingStatus[deviceId];

      return (
        <Card
          key={deviceId}
          sx={{
            mb: 2,
            border: isItemSelected ? 2 : 1,
            borderColor: isItemSelected ? 'primary.main' : 'divider'
          }}>
          
              <CardContent>
                <Stack spacing={2}>
                  {/* Header with checkbox and status */}
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Checkbox
                    color="primary"
                    checked={isItemSelected}
                    onChange={() => handleSelectDevice(deviceId)}
                    sx={{ p: 0 }} />

                      <Box>
                        <Typography variant="h6" component="div">
                          {getDeviceName(device)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {device.deviceTypeModel?.friendlyName || 'Unknown'}
                        </Typography>
                      </Box>
                    </Stack>
                    {pingInfo?.status === 'online' ?
                <Chip
                  icon={<OnlineIcon />}
                  label={t ? t("DeviceStatusDashboard.online") : "Online"}
                  color="success"
                  size="small" /> :

                pingInfo?.status === 'failed' ?
                <Chip
                  icon={<OfflineIcon />}
                  label={t ? t("DeviceTable.offline") : "Offline"}
                  color="error"
                  size="small" /> :


                getStatusChip(device.config)
                }
                  </Stack>

                  {/* Hexnode Actions - Prominent placement for tablets */}
                  {(device.deviceType === 9) && canManageDevices && (
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      p: 1.5,
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                      borderRadius: 1,
                      border: '1px dashed',
                      borderColor: 'primary.main'
                    }}>
                      <HexnodeActions device={device} onActionComplete={onRefresh} />
                    </Box>
                  )}

                  <Divider />

                  {/* Device Details - Improved grid layout */}
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t ? t("DeviceTable.ip_address") : "IP Address"}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {getDeviceLanIP(device) || getDeviceWanIP(device) || 'N/A'}
                      </Typography>
                    </Grid>

                    {pingInfo && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t ? t("DeviceTable.last_ping") : "Last Ping"}
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {pingInfo.timestamp ? new Date(pingInfo.timestamp).toLocaleTimeString() : '-'}
                          {pingInfo.responseTime && (
                            <Chip
                              label={`${pingInfo.responseTime}ms`}
                              size="small"
                              sx={{ ml: 0.5, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Stack>
              </CardContent>

              {canManageDevices &&
          <CardActions sx={{
            flexDirection: 'column',
            gap: 1,
            px: 2,
            pb: 2,
            alignItems: 'stretch'
          }}>
                  {/* Action Buttons Row */}
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={pinging[deviceId] ? <CircularProgress size={16} /> : <RefreshIcon />}
                      onClick={() => handlePingDevice(deviceId)}
                      disabled={pinging[deviceId]}
                      sx={{ minWidth: 80 }}
                    >
                      {t ? t("DeviceTable.ping") : "Ping"}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => onEditDevice(device)}
                      sx={{ minWidth: 80 }}
                    >
                      {t ? t("DeviceTable.edit") : "Edit"}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => onDeleteDevice(device)}
                      sx={{ minWidth: 80 }}
                    >
                      {t ? t("DeviceTable.delete") : "Delete"}
                    </Button>
                  </Stack>
                </CardActions>
          }
            </Card>);

    })
    }
    </Box>;


  // Mobile View
  if (isMobile) {
    return renderMobileView();
  }

  // Desktop Table View
  return (
    <Box>
      {selected.length > 0 &&
      <Toolbar
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
          ...(selected.length > 0 && {
            bgcolor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity)
          })
        }}>
        
          <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div">
          
            {selected.length}{t ? t("DeviceTable.selected") : "selected"}
        </Typography>

          <Tooltip title={t ? t("DeviceTable.ping_selected_devices") : "Ping Selected Devices"}>
            <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handlePingSelected}
            size="small">{t ? t("DeviceTable.ping_all") : "Ping All"}


          </Button>
          </Tooltip>
        </Toolbar>
      }

      <TableContainer component={Paper}>
        <Table aria-label={t ? t("DeviceTable.device_table") : "device table"}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  indeterminate={selected.length > 0 && selected.length < devices.length}
                  checked={devices.length > 0 && selected.length === devices.length}
                  onChange={handleSelectAllClick} />
                
              </TableCell>
              <TableCell>{t ? t("DeviceTable.status") : "Status"}</TableCell>
              <TableCell>{t ? t("DeviceTable.device_name") : "Device Name"}</TableCell>
              <TableCell>{t ? t("DeviceTable.last_ping") : "Last Ping"}</TableCell>
              <TableCell>{t ? t("DeviceTable.response_time") : "Response Time"}</TableCell>
              <TableCell>{t ? t("DeviceTable.type") : "Type"}</TableCell>
              <TableCell>{t ? t("DeviceTable.ip_address") : "IP Address"}</TableCell>
              {canManageDevices && <TableCell align="right">{t ? t("DeviceTable.actions") : "Actions"}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.map((device) => {
              const deviceId = device.id || device._id || device.device_UUID;
              const isItemSelected = isSelected(deviceId);
              const pingInfo = pingStatus[deviceId];

              return (
                <TableRow
                  key={deviceId}
                  hover
                  onClick={() => handleSelectDevice(deviceId)}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  selected={isItemSelected}>
                  
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={isItemSelected} />
                    
                  </TableCell>
                  <TableCell>
                    {pingInfo?.status === 'online' ?
                    <Chip
                      icon={<OnlineIcon />}
                      label={t ? t("DeviceStatusDashboard.online") : "Online"}
                      color="success"
                      size="small"
                      variant="outlined" /> :

                    pingInfo?.status === 'failed' ?
                    <Chip
                      icon={<OfflineIcon />}
                      label={t ? t("DeviceTable.offline") : "Offline"}
                      color="error"
                      size="small"
                      variant="outlined" /> :


                    getStatusChip(device.config)
                    }
                  </TableCell>
                  <TableCell>{getDeviceName(device)}</TableCell>
                  <TableCell>
                    {pingInfo?.timestamp ?
                    new Date(pingInfo.timestamp).toLocaleTimeString() :
                    '-'
                    }
                  </TableCell>
                  <TableCell>
                    {pingInfo?.responseTime ?
                    `${pingInfo.responseTime}ms` :
                    '-'
                    }
                  </TableCell>
                  <TableCell>{device.deviceTypeModel?.friendlyName || 'Unknown'}</TableCell>
                  <TableCell>{getDeviceLanIP(device) || getDeviceWanIP(device) || 'N/A'}</TableCell>
                  {canManageDevices &&
                  <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                        {/* Hexnode Actions for Android Tablets */}
                        {(device.deviceType === 9) && (
                          <HexnodeActions device={device} onActionComplete={onRefresh} />
                        )}

                        <Tooltip title={t ? t("DeviceTable.ping_device") : "Ping Device"}>
                          <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePingDevice(deviceId);
                          }}
                          disabled={pinging[deviceId]}
                          aria-label={t ? t("DeviceTable.ping_device_1") : "ping device"}>

                            {pinging[deviceId] ?
                          <CircularProgress size={20} /> :

                          <RefreshIcon />
                          }
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t ? t("DeviceTable.edit_device") : "Edit Device"}>
                          <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditDevice(device);
                          }}
                          aria-label={t ? t("DeviceTable.edit_device_1") : "edit device"}>

                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t ? t("DeviceTable.delete_device") : "Delete Device"}>
                          <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteDevice(device);
                          }}
                          aria-label={t ? t("DeviceTable.delete_device_1") : "delete device"}>

                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  }
                </TableRow>);

            })}
            {devices.length === 0 &&
            <TableRow>
                <TableCell colSpan={canManageDevices ? 8 : 7} align="center">
                  <Typography variant="body2" color="textSecondary">{t ? t("DeviceTable.no_devices_found") : "No devices found"}

                </Typography>
                </TableCell>
              </TableRow>
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Box>);

};

export default DeviceTable;